import type { CopilotActionView } from "@/features/copilot/schemas/copilot.schema";

/** An action whose change is applying, or may still land after an unconfirmed result. */
export function isCopilotActionExecuting(action: CopilotActionView): boolean {
  return action.status === "running" || (action.status === "unknown" && action.reconciling);
}

/**
 * Mirrors the shore's `hasBlockingActions`: the server answers 409 to a new
 * message while any action is pending or executing, and to nothing else.
 */
export function hasBlockingCopilotActions(actions: Iterable<CopilotActionView>): boolean {
  for (const action of actions) {
    if (action.status === "pending" || isCopilotActionExecuting(action)) return true;
  }
  return false;
}

/** The shore refuses a decision while another action of the conversation is executing. */
export function hasExecutingSiblingAction(
  actions: Iterable<CopilotActionView>,
  toolCallId: string,
): boolean {
  for (const action of actions) {
    if (action.toolCallId !== toolCallId && isCopilotActionExecuting(action)) return true;
  }
  return false;
}

export type CopilotBlockingNotice =
  | { kind: "decide"; toolCallId: string }
  | { kind: "wait" }
  | null;

/** What the composer tells the person while it is locked: decide on a card, or wait for a write. */
export function copilotBlockingNotice(actions: Iterable<CopilotActionView>): CopilotBlockingNotice {
  let waiting = false;
  for (const action of actions) {
    if (action.status === "pending") return { kind: "decide", toolCallId: action.toolCallId };
    if (isCopilotActionExecuting(action)) waiting = true;
  }
  return waiting ? { kind: "wait" } : null;
}
